import React, { memo, useMemo, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useInlineEditFieldInjections } from '@atlassian/jira-issue-field-injections/src/controllers/inline-edit-injections-context/index.tsx';
import type { linkRelationshipType_nativeIssueTable_LinkRelationshipTypeCell$key } from '@atlassian/jira-relay/src/__generated__/linkRelationshipType_nativeIssueTable_LinkRelationshipTypeCell.graphql';
import { FieldInlineEditLiteWithEntryPoint } from '@atlassian/jira-issue-field-inline-edit-lite/src/ui/index.tsx';
import { useFieldInlineEditActions } from '@atlassian/jira-issue-field-inline-edit-actions/src/controllers/index.tsx';
import type { ValidationFieldProps } from '@atlassian/jira-issue-field-validation-popup/src/ui/types.tsx';
import { useLazyLoadEditability } from '../../../../services/lazy-editability-loader/index.tsx';
import RelationshipTypeEditViewEntryPoint from './edit-view/entrypoint.tsx';
import type { NullableOption, LinkRelationshipTypeOption } from './types.tsx';
import { LinkRelationshipTypeReadView } from './read-view/index.tsx';
import { createSelectedOptionFromFieldData } from './utils.tsx';

export type Props = {
	fieldRef: linkRelationshipType_nativeIssueTable_LinkRelationshipTypeCell$key;
};

const isEqualValue = (a: NullableOption, b: NullableOption) => a?.value === b?.value;

export const LinkRelationshipTypeCell = memo<Props>(({ fieldRef }) => {
	const { overriding } = useInlineEditFieldInjections();

	const data = useFragment<linkRelationshipType_nativeIssueTable_LinkRelationshipTypeCell$key>(
		graphql`
			fragment linkRelationshipType_nativeIssueTable_LinkRelationshipTypeCell on JiraIssueLinkRelationshipTypeField
			@argumentDefinitions(isInlineEditingEnabled: { type: "Boolean!" }) {
				id
				fieldId
				type
				name
				isEditableInIssueView @include(if: $isInlineEditingEnabled)
				issueLink {
					id
					direction
					relationName
					type {
						id
					}
				}
				...readView_nativeIssueTable_LinkRelationshipTypeReadView
			}
		`,
		fieldRef,
	);

	const initialValue = useMemo(() => {
		const issueLinkData = {
			issueLinkTypeARI: data?.issueLink?.type?.id ?? '',
			relationName: data?.issueLink?.relationName ?? '',
			direction: data?.issueLink?.direction ?? '',
		};

		return createSelectedOptionFromFieldData(issueLinkData);
	}, [data?.issueLink]);
	const [selectedOption, setSelectedOption] = useState<NullableOption>(initialValue);

	const baseIsEditable = 'isEditableInIssueView' in data ? data.isEditableInIssueView : undefined;
	useLazyLoadEditability(data.id, baseIsEditable);

	const isEditable = useMemo(
		() => overriding.overrideIsEditable(Boolean(baseIsEditable)),
		[baseIsEditable, overriding],
	);

	const handleSubmit = useCallback(() => {
		// TODO: Implement submit
	}, []);

	const {
		hasServerValidationError,
		handleCancel,
		handleConfirm,
		handleChangeAndConfirm,
		handleEdit,
		invalidMessage,
		isEditing,
	} = useFieldInlineEditActions<NullableOption, undefined>({
		fieldId: data.fieldId,
		fieldName: data.name,
		fieldType: data.type,
		initialValue,
		updatedValue: selectedOption,
		isValueEqual: isEqualValue,
		clipboardComponentType: undefined,
		onSubmit: handleSubmit,
		onUpdateValue: setSelectedOption,
	});

	const readView = useCallback(() => <LinkRelationshipTypeReadView fieldRef={data} />, [data]);

	const handleUnlink = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			handleCancel(analyticsEvent);

			// TODO: Implement unlink
		},
		[handleCancel],
	);

	const getEditViewProps = useCallback(
		(fieldProps: ValidationFieldProps) => ({
			...fieldProps,
			selectedOption,
			isDisabled: !isEditable,
			onChange: (value: LinkRelationshipTypeOption) => {
				handleChangeAndConfirm(value);
			},
			onUnlink: handleUnlink,
		}),
		[handleChangeAndConfirm, handleUnlink, isEditable, selectedOption],
	);

	return (
		<FieldInlineEditLiteWithEntryPoint
			editViewPopup
			editViewPopupAlignBlock="center"
			editViewPopupMinWidth="small"
			editViewEntryPoint={RelationshipTypeEditViewEntryPoint}
			editViewEntryPointParams={{}}
			getEditViewProps={getEditViewProps}
			fieldName={data.name}
			isEditable={isEditable}
			readView={readView}
			invalidMessage={invalidMessage}
			isEditing={isEditing}
			hasUnsubmittedChanges={hasServerValidationError}
			onEdit={handleEdit}
			onCancel={handleCancel}
			onConfirm={handleConfirm}
			readViewFitContainerHeight
			hideActionButtons
		/>
	);
});
